<script setup lang="ts">
import type { PolicyDocumentAction, PolicyFieldRules, PolicyItemRules, PolicyValidationRules } from '#layers/access/types/permissions/policy';
import type { MonospaceCollection, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { useOverlay } from '@nuxt/ui/composables';
import { createColumnHelper } from '@tanstack/vue-table';
import { tv } from 'tailwind-variants';
import { computed, h, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import nuxtUiButtonTheme from '#build/ui/button';
import { DButton, DTooltip } from '#components';
import EntitlementGateNotice from '#layers/access/components/License/EntitlementGateNotice.vue';
import { usePolicyRules } from '#layers/access/composables/policy/use-policy-rules';
import { allPolicyDocumentActions, isFieldRulesAction, isItemRulesAction } from '#layers/access/utils/policies';
import { useExtensionsStore } from '#layers/extensions/public';
import { useLicenseEntitlementGate } from '#layers/license/public';
import { ButtonDestructive, showConfirmationDialog, useMergedTheme } from '#layers/ui/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';
import PolicyActionCell from './PolicyActionCell.vue';
import PolicyCollectionDrawer from './PolicyCollectionDrawer.vue';
import PolicyColumnFieldSelect from './PolicyColumnFieldSelect.vue';
import PolicyOverrideConfirmDialog from './PolicyOverrideConfirmDialog.vue';
import PolicyRulesDrawer from './PolicyRulesDrawer.vue';

const overlay = useOverlay();
const { t } = useI18n();

const { isGated: isCustomRulesGated } = useLicenseEntitlementGate('custom_permission_rules_enabled');

const itemRules = defineModel<PolicyItemRules>('itemRules', { required: true });
const fieldRules = defineModel<PolicyFieldRules>('fieldRules', { required: true });
const validationRules = defineModel<PolicyValidationRules>('validationRules', { required: true });

const {
	// General
	content,
	setAllAccess,
	deleteAllAccess,
	getRuleInformation,

	// Item rules
	setItemRules,
	createItemRule,
	deleteItemRule,

	// Field rules
	setFieldRules,
	setFieldRulesWildcard,
	deleteFieldRules,

	// Validation rules
	setValidationRules,
	deleteValidationRules,
	getValidationRulesForCollection,
} = usePolicyRules({
	itemRules,
	fieldRules,
	validationRules,
});

const appConfig = useAppConfig();

const selectedCollection = ref(null);
const addedCollections = ref<ResolvedMonospaceCollection[]>([]);

const schemaStore = useWorkspaceSchemaStore();
const extensionsStore = useExtensionsStore();

function isNonNativeConnectorCollection(collection: ResolvedMonospaceCollection) {
	const source = schemaStore.findSourceById(collection.sourceId);
	if (!source) return false;

	return extensionsStore.dataConnectorProviderIds.includes(source.provider);
}

function isCustomRulesDisabled(collection: ResolvedMonospaceCollection) {
	return isCustomRulesGated.value || isNonNativeConnectorCollection(collection);
}

function getCustomRulesDisabledTooltip(collection: ResolvedMonospaceCollection) {
	if (isCustomRulesGated.value) return t('policy.policy_rules.custom_gated_tooltip');

	if (isNonNativeConnectorCollection(collection)) {
		return t('policy.policy_rules.custom_non_native_connector_tooltip');
	}
}

/**
 * A collection belongs here once the policy says anything about it, validation included — a rule
 * that rejects a write is still this policy's, and a collection carrying only those would
 * otherwise be listed nowhere and its rules reachable from nowhere.
 *
 * System collections are not filtered out: a policy that already names one has to keep showing it,
 * whatever the picker is willing to offer.
 */
const listedCollections = computed(() => {
	const named = new Set([
		...Object.keys(content.value.itemRules ?? {}),
		...Object.keys(content.value.fieldRules ?? {}),
		...Object.keys(content.value.validationRules ?? {}),
	]);

	return schemaStore.collections.filter((collection) =>
		named.has(collection.apiName) || addedCollections.value.some((added) => added.apiName === collection.apiName));
});

async function confirmCustomOverride(collection: MonospaceCollection, action?: PolicyDocumentAction): Promise<boolean> {
	const isCustom = action
		? getRuleInformation(collection, action).type === 'custom'
		: allPolicyDocumentActions.some((a) => getRuleInformation(collection, a).type === 'custom');

	if (!isCustom) return true;

	return !!await showConfirmationDialog({
		component: PolicyOverrideConfirmDialog,
		props: {
			collectionName: collection.apiName,
			action: action ? t(`policy.action.${action}`).toLowerCase() : undefined,
		},
	});
}

async function handleAllAccess(collection: MonospaceCollection, action: PolicyDocumentAction) {
	if (!await confirmCustomOverride(collection, action)) return;

	if (isItemRulesAction(action)) {
		createItemRule({ action, collectionName: collection.apiName });
	}

	if (isFieldRulesAction(action)) {
		setFieldRulesWildcard({ action, collectionName: collection.apiName });
	}
}

async function handleNoAccess(collection: MonospaceCollection, action: PolicyDocumentAction) {
	if (!await confirmCustomOverride(collection, action)) return;

	deleteItemRule({ action, collectionName: collection.apiName });
	deleteFieldRules({ action, collectionName: collection.apiName });
}

async function handleSelectAllForAllActions(collection: MonospaceCollection) {
	if (!await confirmCustomOverride(collection)) return;
	setAllAccess(collection.apiName);
}

async function handleSelectNoneForAllActions(collection: MonospaceCollection) {
	if (!await confirmCustomOverride(collection)) return;
	deleteAllAccess(collection.apiName);
}

async function handleCustomAccess(collection: ResolvedMonospaceCollection, action: PolicyDocumentAction) {
	if (isCustomRulesDisabled(collection)) return;

	const resolvedCollection = schemaStore.getCollection(collection.id);

	if (!resolvedCollection) {
		return;
	}

	const modal = overlay.create(PolicyRulesDrawer, {
		props: {
			action,
			collectionId: collection.id,
			content: content.value,
		},
	});
	const instance = modal.open();
	const payload = await instance.result as { data: any } | undefined;

	if (payload?.data) {
		if (payload.data.itemRules) {
			setItemRules({ collectionName: collection.apiName, action, rules: payload.data.itemRules });
		}

		if (payload.data.fieldRules && Object.keys(payload.data.fieldRules).length > 0) {
			setFieldRules({ collectionName: collection.apiName, action, rules: payload.data.fieldRules });
		}
	}
}

async function handleValidationRules(collection: MonospaceCollection) {
	const modal = overlay.create(PolicyCollectionDrawer, {
		props: {
			collectionId: collection.id,
			content: content.value,
		},
	});
	const instance = modal.open();
	const payload = await instance.result as { data: any } | undefined;

	if (payload?.data?.validationRules) {
		setValidationRules({ collectionName: collection.apiName, collectionValidationRules: payload.data.validationRules });
	}
}

function handleChangeSelect() {
	if (selectedCollection.value) {
		const resolvedCollection = schemaStore.getCollection(selectedCollection.value);

		if (resolvedCollection) {
			addedCollections.value.push(resolvedCollection);
		}

		selectedCollection.value = null;
	}
}

function handleRemove(collection: MonospaceCollection) {
	deleteAllAccess(collection.apiName);
	deleteValidationRules({ collectionName: collection.apiName });
	addedCollections.value = addedCollections.value.filter((c) => c.apiName !== collection.apiName);
}

function createActionColumnHelperDisplay(action: PolicyDocumentAction) {
	return createColumnHelper<ResolvedMonospaceCollection>().display({
		id: 'action',
		header: () => h('div', { class: 'text-center w-full' }, t(`policy.action.${action}`)),
		enableHiding: false,
		enableResizing: false,
		size: 88,
		cell({ row }) {
			const customDisabledTooltip = getCustomRulesDisabledTooltip(row.original);

			return h('div', { class: 'flex w-full h-full' }, h(PolicyActionCell, {
				action,
				collectionName: row.original.apiName,
				ruleInformation: getRuleInformation(row.original, action),
				customDisabled: customDisabledTooltip !== undefined,
				customDisabledTooltip,
				onAllAccess: () => handleAllAccess(row.original, action),
				onNoAccess: () => handleNoAccess(row.original, action),
				onCustomAccess: () => handleCustomAccess(row.original, action),
			}));
		},
	});
}

const columns = [
	createColumnHelper<ResolvedMonospaceCollection>().display({
		id: 'apiName',
		header: t('common.collections'),
		enableHiding: false,
		enableResizing: true,
		size: 140,
		meta: {
			class: {
				th: 'ps-4 pe-2',
				td: 'ps-4 pe-2',
			},
		},
		cell({ row }) {
			const collectionName = h(DTooltip, {
				text: row.original.apiName,
				delayDuration: 0,
				content: { side: 'bottom' },
				class: 'min-w-0',
				ui: { text: 'font-mono' },
			}, [h('span', { class: 'font-mono font-medium truncate min-w-0 block' }, row.original.apiName)]);
			const validationRules = getValidationRulesForCollection({ collectionName: row.original.apiName });
			const validationRulesButton = h(DButton, {
				color: 'primary',
				icon: 'lucide:shield-check',
				variant: 'soft',
				size: 'xs',
				onClick: () => handleValidationRules(row.original),
			}, [validationRules.length]);
			const validationRulesTooltipWithButton = h(DTooltip, {
				text: t('policy.validation_rules.n_validation_rules', validationRules.length),
				delayDuration: 0,
				content: { side: 'bottom' },
				class: 'shrink-0',
			}, [validationRulesButton]);
			return h('div', { class: 'flex gap-2 items-center min-w-0' }, [
				collectionName,
				validationRulesTooltipWithButton,
			]);
		},
	}),

	createColumnHelper<ResolvedMonospaceCollection>().display({
		id: 'handleAllActions',
		header: '',
		enableHiding: false,
		enableResizing: false,
		size: 84,
		cell({ row }) {
			return h(PolicyColumnFieldSelect, {
				class: 'opacity-0 group-hover/row:opacity-100 flex flex-1 justify-end',
				onSelectAll: () => handleSelectAllForAllActions(row.original),
				onSelectNone: () => handleSelectNoneForAllActions(row.original),
			});
		},
	}),

	...allPolicyDocumentActions.map((action) => createActionColumnHelperDisplay(action)),
	createColumnHelper<ResolvedMonospaceCollection>().display({
		id: 'actions',
		header: '',
		enableHiding: false,
		enableResizing: false,
		size: 40,
		cell({ row }) {
			const removeButton = h(ButtonDestructive, {
				class: 'opacity-0 group-hover/row:opacity-100 text-dimmed',
				color: 'neutral',
				icon: appConfig.ui.icons.remove,
				onClick: () => handleRemove(row.original),
				variant: 'link',
			});
			const tooltip = h(DTooltip, {
				text: t('policy.policy_rules.remove_tooltip'),
				delayDuration: 0,
				content: { side: 'top' },
			}, [removeButton]);
			return h('div', { class: 'flex justify-end' }, [tooltip]);
		},
		meta: {
			class: {
				td: 'flex justify-end',
			},
		},
	}),
];

onMounted(() => {
	// Ensures that already added collections stay displayed, even after the last rule is removed.
	addedCollections.value = listedCollections.value;
});

const buttonTheme = useMergedTheme({ button: nuxtUiButtonTheme });
const collectionSelectUi = tv(buttonTheme)({ variant: 'outline', color: 'neutral', size: 'sm' });
</script>

<template>
	<div class="flex flex-col gap-4">
		<EntitlementGateNotice entitlement-key="custom_permission_rules_enabled" />
		<DNotice color="neutral" :description="$t('policy.policy_rules.explainer')" />
		<DDataTable
			:data="listedCollections"
			:columns
			fill-column="apiName"
			:ui="{ base: 'w-full overflow-x-auto', th: 'px-1', td: 'px-1' }"
			hide-pagination
		>
			<template #actions>
				<CollectionSelect
					v-model="selectedCollection"
					data-testid="policy-create-permission"
					label-key="apiName"
					trailing-icon=""
					:filter="{ id: { _nin: listedCollections.map(c => c.id) } }"
					:leading-icon="appConfig.ui.icons.plus"
					:ui="{
						base: collectionSelectUi.base({ class: 'font-sans!' }),
						leadingIcon: 'text-normal',
					}"
					size="sm"
					@change="handleChangeSelect"
				>
					{{ $t('policy.policy_rules.create_permission') }}
				</CollectionSelect>
			</template>
		</DDataTable>
	</div>
</template>
